import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  CancelOutlined as CancelIcon,
  CheckCircleOutline as CheckIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  MoreVert as MoreIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../../services/transactionApi';
import { settledSummary } from '../../services/settlement';
import { useTransactions } from '../../contexts/TransactionContext';
import { useDashboardCategories } from '../../hooks/useDashboardCategories';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { useAuth } from '../../contexts/AuthContext';
import { canModifyTransaction } from '../../utils/contextPermissions';
import { localTodayIso, parseLocalDate } from '../../utils/bills';
import { formatMoneyCents } from '../../theme/chart';
import { tokens } from '../../theme/tokens';
import FinMark from '../brand/FinMark';
import { periodRange } from './periods';

interface TransactionListProps {
  onEditTransaction: (transaction: Transaction) => void;
  /** set when the period or filters leave nothing to show */
  onClearFilters?: () => void;
  now?: Date;
}

/** Entries that came through the bot: text, voice or a photo sent to Fin. */
const FROM_FIN = new Set<Transaction['inputMethod']>(['telegram', 'voice', 'ocr']);

const Flag: React.FC<{ tone: 'accent' | 'warn' | 'muted'; children: React.ReactNode; icon?: React.ReactNode }> = ({ tone, children, icon }) => (
  <Box
    component='span'
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.6,
      px: 1,
      py: 0.25,
      borderRadius: 99,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      bgcolor: tone === 'accent' ? tokens.accentSoft : tone === 'warn' ? tokens.warnSoft : tokens.bg,
      color: tone === 'accent' ? tokens.accent : tone === 'warn' ? tokens.warn : tokens.ink3,
    }}
  >
    {icon}
    {children}
  </Box>
);

/**
 * Lançamentos as a list grouped by day, newest first: what each entry was,
 * where it came from (the bot, a split, a payer), what it cost the user,
 * and its actions. One layout for every screen, so nothing scrolls sideways.
 */
export const TransactionList: React.FC<TransactionListProps> = ({ onEditTransaction, onClearFilters, now = new Date() }) => {
  const { t, i18n } = useTranslation('transactions');
  const locale = i18n.language;
  const { state, loadTransactions, deleteTransaction, confirmTransaction, cancelTransaction } = useTransactions();
  const { mapToDisplayCategory } = useDashboardCategories();
  const { selectedContext } = useFinancialContexts();
  const { state: authState } = useAuth();
  const userId = authState.user?.id;
  const inGroup = Boolean(selectedContext && selectedContext.type !== 'personal');

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [deleting, setDeleting] = useState<Transaction | null>(null);

  const money = (value: number, currency: string) => formatMoneyCents(value, currency, locale);

  // In a group, entries recorded by other members change only by an owner or admin, as the API allows.
  const mayModify = (transaction: Transaction | null): boolean =>
    Boolean(transaction) &&
    canModifyTransaction({ transactionUserId: transaction!.userId, currentUserId: userId, contextRole: selectedContext?.memberRole });

  const closeMenu = () => {
    setAnchor(null);
    setSelected(null);
  };

  const act = async (action: (id: string) => Promise<unknown>, transaction: Transaction | null) => {
    if (!transaction) return;
    try {
      await action(transaction.id);
      await loadTransactions(state.filters);
    } catch (error) {
      console.error('Transaction action failed:', error);
    }
  };

  const dayLabel = (iso: string) => {
    const date = parseLocalDate(iso);
    const today = localTodayIso(now);
    const yesterday = localTodayIso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
    if (iso === today) return t('list.today');
    if (iso === yesterday) return t('list.yesterday');
    return date.toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
    });
  };

  /** What it cost the user: their part in the personal view, the whole in a group. */
  const costOf = (transaction: Transaction) =>
    !inGroup && transaction.personalAmount !== undefined ? Number(transaction.personalAmount) : Number(transaction.amount);

  /** Under the description: who paid, how it was split, what was settled on the spot. */
  const details = (transaction: Transaction): string[] => {
    if (transaction.type === 'transfer' && transaction.metadata?.settlement) return [t('list.settlement')];
    const parts: string[] = [];
    if (inGroup && transaction.user && transaction.userId !== userId) {
      parts.push(t('list.recordedBy', { name: transaction.user.firstName }));
    }
    if (transaction.paidByUserId && transaction.paidByUserId !== userId) {
      parts.push(t('list.paidBy', { name: transaction.paidBy?.firstName ?? '?' }));
    }
    const shares = transaction.shares ?? [];
    if (shares.length > 1) {
      const own = shares.find((share) => share.userId === userId);
      parts.push(inGroup && own ? t('list.yourShare', { amount: money(Number(own.amount), transaction.currency) }) : t('list.splitAmong', { count: shares.length }));
      const settled = settledSummary(transaction);
      if (settled?.kind === 'all') {
        parts.push(t('list.settledOnSpot'));
      } else if (settled?.kind === 'partial') {
        const who = settled.entries
          .map((entry) => `${shares.find((share) => share.userId === entry.userId)?.user?.firstName ?? '?'} ${money(entry.amount, transaction.currency)}`)
          .join(', ');
        parts.push(t('list.paidOnSpot', { who }));
      }
    }
    return parts;
  };

  // Newest first, in days.
  const days: Array<{ date: string; items: Transaction[] }> = [];
  state.transactions.forEach((transaction) => {
    const date = String(transaction.date).slice(0, 10);
    const last = days[days.length - 1];
    if (last && last.date === date) last.items.push(transaction);
    else days.push({ date, items: [transaction] });
  });

  const dayNet = (items: Transaction[]) =>
    items
      .filter((item) => item.status !== 'cancelled')
      .reduce((sum, item) => sum + (item.type === 'income' ? costOf(item) : item.type === 'expense' ? -costOf(item) : 0), 0);

  const panel = { bgcolor: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: `${tokens.radius.card}px` } as const;

  if (state.isLoading && state.transactions.length === 0) {
    return (
      <Box sx={{ ...panel, p: 2.5, display: 'grid', gap: 1.5 }}>
        {[0, 1, 2, 3].map((index) => <Skeleton key={index} variant='rounded' height={52} />)}
      </Box>
    );
  }

  // Anything narrowed beyond the default "this month"?
  const month = periodRange('thisMonth', now);
  const f = state.filters;
  const filtered =
    Boolean(f.type || f.status || f.dashboardCategory || f.merchantName || f.search) ||
    f.minAmount !== undefined ||
    f.maxAmount !== undefined ||
    f.startDate !== month.startDate ||
    f.endDate !== month.endDate;

  if (state.transactions.length === 0) {
    return (
      <Box sx={{ ...panel, px: 3, py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}>
        <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 19, fontWeight: 650 }}>{t('list.emptyState.title')}</Box>
        <Box component='p' sx={{ m: 0, color: tokens.ink3, maxWidth: 420 }}>{t('list.emptyState.subtitle')}</Box>
        {onClearFilters && filtered && (
          <Button variant='outlined' onClick={onClearFilters} sx={{ mt: 1 }}>
            {t('filters.clear')}
          </Button>
        )}
      </Box>
    );
  }

  const first = (state.page - 1) * state.limit + 1;
  const last = Math.min(state.page * state.limit, state.total);
  const pages = Math.max(1, Math.ceil(state.total / state.limit));
  const currency = selectedContext?.defaultCurrency || authState.user?.defaultCurrency || 'BRL';

  return (
    <Box component='section' aria-label={t('list.title')} sx={{ ...panel, overflow: 'hidden', opacity: state.isLoading ? 0.6 : 1, transition: 'opacity .2s' }}>
      {days.map((day) => {
        const net = dayNet(day.items);
        return (
          <Box key={day.date} component='section' aria-label={dayLabel(day.date)} sx={{ '& + section': { borderTop: `1px solid ${tokens.line}` } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                px: { xs: 2, md: 2.75 },
                py: 1.25,
                bgcolor: tokens.bg,
                borderBottom: `1px solid ${tokens.line}`,
              }}
            >
              <Box component='h3' sx={{ m: 0, fontSize: 13.5, fontWeight: 700, color: tokens.ink2, '&::first-letter': { textTransform: 'uppercase' } }}>
                {dayLabel(day.date)}
              </Box>
              {Math.abs(net) >= 0.005 && (
                <Box component='span' sx={{ fontSize: 13, color: tokens.ink3, fontVariantNumeric: 'tabular-nums' }}>
                  {net > 0 ? '+' : '−'}
                  {money(Math.abs(net), currency)}
                </Box>
              )}
            </Box>

            <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {day.items.map((transaction, index) => {
                const income = transaction.type === 'income';
                const transfer = transaction.type === 'transfer';
                const cancelled = transaction.status === 'cancelled';
                const category = transaction.category ? mapToDisplayCategory(transaction.type, transaction.category).name : '';
                const cost = costOf(transaction);
                const whole = Number(transaction.amount);
                const showWhole = !inGroup && Math.abs(cost - whole) >= 0.005;
                const fromFin = FROM_FIN.has(transaction.inputMethod);
                const sub = [transaction.merchantName, category].filter(Boolean).join(' · ');
                const extra = details(transaction);
                return (
                  <Box
                    component='li'
                    key={transaction.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1.25, md: 1.75 },
                      pl: { xs: 1.75, md: 2.75 },
                      pr: { xs: 0.75, md: 1.5 },
                      py: 1.5,
                      borderTop: index ? `1px solid ${tokens.line}` : 'none',
                      opacity: cancelled ? 0.6 : 1,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: { xs: 34, md: 40 },
                        height: { xs: 34, md: 40 },
                        flexShrink: 0,
                        borderRadius: { xs: '10px', md: '12px' },
                        bgcolor: income ? tokens.accentSoft : tokens.bg,
                        color: income ? tokens.accent : tokens.ink2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {transfer ? <TransferIcon fontSize='small' /> : (category || transaction.description).slice(0, 2).toUpperCase()}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Box component='span' sx={{ fontWeight: 600, overflowWrap: 'anywhere', textDecoration: cancelled ? 'line-through' : 'none' }}>
                        {transaction.description}
                      </Box>
                      {sub && <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink3, overflowWrap: 'anywhere' }}>{sub}</Box>}
                      {extra.length > 0 && <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink2, overflowWrap: 'anywhere' }}>{extra.join(' · ')}</Box>}
                      {(fromFin || transaction.status !== 'confirmed') && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                          {fromFin && <Flag tone='accent' icon={<FinMark size={12} />}>{t('list.byFin')}</Flag>}
                          {transaction.status === 'pending' && <Flag tone='warn'>{t('list.status.pending')}</Flag>}
                          {cancelled && <Flag tone='muted'>{t('list.status.cancelled')}</Flag>}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                      <Box
                        component='strong'
                        sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: income ? tokens.accent : tokens.ink, textDecoration: cancelled ? 'line-through' : 'none' }}
                      >
                        {income ? '+' : transaction.type === 'expense' ? '−' : ''}
                        {money(cost, transaction.currency)}
                      </Box>
                      {showWhole && (
                        <Box component='span' sx={{ fontSize: 12, color: tokens.ink3, whiteSpace: 'nowrap' }}>
                          {t('list.ofTotal', { total: money(whole, transaction.currency) })}
                        </Box>
                      )}
                    </Box>

                    <IconButton
                      aria-label={t('list.actions.moreFor', { name: transaction.description })}
                      onClick={(event) => {
                        setAnchor(event.currentTarget);
                        setSelected(transaction);
                      }}
                      sx={{ width: 40, height: 40, color: tokens.ink3 }}
                    >
                      <MoreIcon fontSize='small' />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 2, md: 2.75 },
          py: 1.25,
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>{t('list.range', { first, last, total: state.total })}</Box>
        {pages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton aria-label={t('list.previous')} disabled={state.page <= 1} onClick={() => loadTransactions({ ...state.filters, page: state.page - 1 })}>
              <PrevIcon />
            </IconButton>
            <Box component='span' sx={{ fontSize: 13, color: tokens.ink2, minWidth: 64, textAlign: 'center' }}>{t('list.page', { page: state.page, pages })}</Box>
            <IconButton aria-label={t('list.next')} disabled={state.page >= pages} onClick={() => loadTransactions({ ...state.filters, page: state.page + 1 })}>
              <NextIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {mayModify(selected) ? (
          <MenuItem onClick={() => { if (selected) onEditTransaction(selected); closeMenu(); }}>
            <ListItemIcon><EditIcon fontSize='small' /></ListItemIcon>
            <ListItemText>{t('list.actions.edit')}</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem disabled sx={{ whiteSpace: 'normal', maxWidth: 280 }}>
            <ListItemText>{t('list.actions.readOnly')}</ListItemText>
          </MenuItem>
        )}
        {mayModify(selected) && selected?.status === 'pending' && (
          <MenuItem onClick={() => { act(confirmTransaction, selected); closeMenu(); }}>
            <ListItemIcon><CheckIcon fontSize='small' /></ListItemIcon>
            <ListItemText>{t('list.actions.confirm')}</ListItemText>
          </MenuItem>
        )}
        {mayModify(selected) && selected?.status !== 'cancelled' && (
          <MenuItem onClick={() => { act(cancelTransaction, selected); closeMenu(); }}>
            <ListItemIcon><CancelIcon fontSize='small' /></ListItemIcon>
            <ListItemText>{t('list.actions.cancel')}</ListItemText>
          </MenuItem>
        )}
        {mayModify(selected) && (
          <MenuItem onClick={() => { setDeleting(selected); closeMenu(); }} sx={{ color: tokens.danger }}>
            <ListItemIcon><DeleteIcon fontSize='small' sx={{ color: tokens.danger }} /></ListItemIcon>
            <ListItemText>{t('list.actions.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth='xs' fullWidth>
        <DialogTitle>{t('list.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('list.deleteDialog.text', { name: deleting?.description ?? '' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>{t('list.deleteDialog.keep')}</Button>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              act(deleteTransaction, deleting);
              setDeleting(null);
            }}
          >
            {t('list.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
